/**
 * Хаб настроек кабинета (ТЗ §14.1): название и адрес, публикация, переходы во вложенные
 * экраны — услуги, специалисты, часы, отпуска, «Правила и антиспам».
 *
 * `slug` и отрасль показаны ТОЛЬКО НА ЧТЕНИЕ. Их нет в `UpdateBusinessInput`, а бэкенд
 * отвергает их в теле, а не игнорирует: молча проглоченный `slug` дал бы владельцу
 * уверенность, что публичный адрес сменился, когда на QR-наклейках у клиентов остался
 * старый. Поле ввода, которое ничего не меняет, обещает больше, чем его отсутствие.
 */

import { ReferralBlock } from './ReferralBlock';
import { ShareBlock } from './ShareBlock';
import { useCallback, useState } from 'react';
import { presetFor } from '@app/shared';
import { useAppApi } from '../../app/ApiProvider';
import { useAsync } from '../../hooks/useAsync';
import { industryName } from '../../domain/catalog';
import { cityDistrictLabel } from '../../domain/geo';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { CityPickerSheet } from '../../ui/CityPickerSheet';
import { ChevronDown } from '../../ui/icons';
import { CtaBar, CtaButton, SmallButton } from '../../ui/Cta';
import { Tag } from '../../ui/controls';
import { Alert, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, SectionTitle, TopBar } from '../../ui/Screen';
import { useBusiness } from './useBusiness';
import { Card, Field, Hint, NavRow, ValueRow } from './profile/fields';
import {
  ADDRESS_MAX_LENGTH,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  buildProfilePatch,
  profileDraftFrom,
  type ProfileDraft,
} from './profile/businessProfile';

/** Экраны, на которые уводит хаб. Совпадает с именами маршрутов кабинета. */
export type SettingsTarget =
  | 'bizServices'
  | 'bizStaff'
  | 'bizSchedule'
  | 'bizTimeOff'
  | 'bizRules'
  | 'bizBlacklist';

const TARGETS: readonly { readonly target: SettingsTarget; readonly labelKey: 'setServices' | 'setStaff' | 'setSchedule' | 'setTimeOff' | 'setRules' | 'setBlacklist' }[] = [
  { target: 'bizServices', labelKey: 'setServices' },
  { target: 'bizStaff', labelKey: 'setStaff' },
  { target: 'bizSchedule', labelKey: 'setSchedule' },
  { target: 'bizTimeOff', labelKey: 'setTimeOff' },
  { target: 'bizRules', labelKey: 'setRules' },
  { target: 'bizBlacklist', labelKey: 'setBlacklist' },
];

export function SettingsScreen(props: {
  businessId: string;
  onBack: () => void;
  onOpen: (target: SettingsTarget) => void;
  onFeedback: () => void;
  /** Организация ушла в историю: экран настроек ей больше не нужен, возвращаемся на «Мой бизнес». */
  onArchived: () => void;
}): JSX.Element {
  const { t, lang } = useI18n();
  const api = useAppApi();
  const { state, business, reload } = useBusiness(props.businessId);

  // `null` — «владелец ничего не правил», и поля показывают серверные значения. После
  // удачного сохранения черновик снова обнуляется: иначе экран продолжал бы показывать
  // набранное, даже если сервер нормализовал его иначе (обрезал пробелы, привёл к null).
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [publishing, setPublishing] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [geoOpen, setGeoOpen] = useState(false);

  const current = business ? draft ?? profileDraftFrom(business) : null;
  const patch = business && current ? buildProfilePatch(business, current) : null;
  const geoLabel = current
    ? cityDistrictLabel(current.city ?? null, current.district ?? null, lang)
    : null;

  const edit = (change: Partial<ProfileDraft>): void => {
    if (!current) return;
    setDraft({ ...current, ...change });
    setSaved(false);
  };

  const save = async (): Promise<void> => {
    if (!business || !patch || !patch.ok || !patch.changed || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.cabinet.updateBusiness(business.id, patch.patch);
      // У частного мастера название организации и есть его имя, а специалист в ней —
      // он сам. Без синхронизации клиент записывался бы к специалисту со старым именем.
      if (business.isSolo && patch.patch.name) {
        const [me] = await api.cabinet.listStaff(business.id);
        if (me) await api.cabinet.updateStaff(business.id, me.id, patch.patch.name, me.title);
      }
      setDraft(null);
      setSaved(true);
      reload();
    } catch (failure) {
      setSaveError(failure);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Публикация и снятие. В каталог организация попадает только по кнопке и только
   * заполненной: хотя бы одна активная услуга и один активный специалист (README,
   * решение 3). Пустую бэкенд отвергает ключами `err.publishRequiresService` /
   * `err.publishRequiresStaff` — оба переведены, поэтому владельцу видно, чего не хватает.
   */
  const togglePublish = async (): Promise<void> => {
    if (!business || publishing) return;
    setPublishing(true);
    setActionError(null);
    try {
      if (business.isPublished) await api.cabinet.unpublishBusiness(business.id);
      else await api.cabinet.publishBusiness(business.id);
      reload();
    } catch (failure) {
      setActionError(failure);
    } finally {
      setPublishing(false);
    }
  };

  /**
   * «В историю». Не удаление: данные остаются, организацию можно восстановить, поэтому
   * подтверждение спокойное, а не красное. Отказ из-за будущих записей приходит ключом
   * `err.businessHasActiveAppointments` и показывается в той же шторке, что и отказ публикации.
   */
  const archive = async (): Promise<void> => {
    if (!business || archiving) return;
    setArchiving(true);
    setActionError(null);
    try {
      await api.cabinet.archiveBusiness(business.id);
      setConfirmArchive(false);
      props.onArchived();
    } catch (failure) {
      setConfirmArchive(false);
      setActionError(failure);
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Screen>
      <TopBar title={t('setTitle')} subtitle={business?.name} onBack={props.onBack} />

      <ScrollArea bottomGap="both">
        {state.status === 'loading' ? <Loader /> : null}
        {state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} /> : null}

        {business && current && patch ? (
          <>
            <div className="px-4 pt-5">
              <div className="mb-2.5 flex items-center gap-2">
                <Tag tone={business.isPublished ? 'ok' : 'new'}>
                  {business.isPublished ? t('bizPublished') : t('bizDraft')}
                </Tag>
              </div>
              {/* Бейдж без объяснения оставлял бы владельца гадать, почему его нет
                  в каталоге. Черновик поясняется текстом, а не значком. */}
              <Alert tone={business.isPublished ? 'plain' : 'warn'}>
                {business.isPublished ? t('setPublishedNote') : t('bizDraftNote')}
              </Alert>
            </div>

            <div className="px-4 pt-3">
              <SectionTitle>{t('setProfile')}</SectionTitle>
              <Field
                label={t(business.isSolo ? 'onbNameSolo' : 'onbName')}
                value={current.name}
                placeholder={t(business.isSolo ? 'onbNameSoloPh' : 'onbNamePh')}
                onChange={(value) => edit({ name: value })}
                error={
                  !patch.ok && patch.errors.name === 'nameShort'
                    ? t('errNameShort', { min: NAME_MIN_LENGTH })
                    : !patch.ok && patch.errors.name === 'nameLong'
                      ? t('errNameLong', { max: NAME_MAX_LENGTH })
                      : undefined
                }
              />
              {business.isSolo ? <SoloTitleField businessId={business.id} /> : null}
              <Field
                label={t('onbAddress')}
                value={current.address}
                placeholder={t('onbAddressPh')}
                onChange={(value) => edit({ address: value })}
                error={
                  !patch.ok && patch.errors.address
                    ? t('errAddressLong', { max: ADDRESS_MAX_LENGTH })
                    : undefined
                }
              />

              {/* Город и район — выбор из справочника, поэтому строка с пикером, а не поле
                  ввода: шторка ведёт город→район, район появляется только у Ташкента. */}
              <div className="mb-3.5">
                <div className="mb-1.5 text-[12.5px] font-semibold text-ink-2">{t('geoCity')}</div>
                <button
                  type="button"
                  onClick={() => setGeoOpen(true)}
                  className="flex w-full items-center justify-between gap-2 rounded-[12px] bg-surface px-3.5 py-3 text-[14px] shadow-[inset_0_0_0_1px_var(--hair)]"
                >
                  <span className={geoLabel ? 'text-ink' : 'text-ink-3'}>
                    {geoLabel ?? t('geoCityNone')}
                  </span>
                  <ChevronDown size={16} strokeWidth={2} className="shrink-0 text-ink-3" aria-hidden="true" />
                </button>
              </div>

              {saveError ? <ErrorState error={saveError} /> : null}
              {saved ? (
                <div className="mb-2 text-[12px] font-semibold text-green">{t('setSaved')}</div>
              ) : null}

              {/* Кнопка сохранения живёт в секции, а не внизу экрана: закреплённая CTA
                  на этом экране одна и занята публикацией (ТЗ §14.3). */}
              <CtaButton
                variant="ghost"
                disabled={saving || !patch.ok || !patch.changed}
                onClick={() => void save()}
              >
                {t('setSave')}
              </CtaButton>
            </div>

            <ShareBlock slug={business.slug} name={business.name} />

            <ReferralBlock businessId={business.id} />

            <div className="px-4 pt-6">
              <SectionTitle>{t('onbType')}</SectionTitle>
              <Card>
                <ValueRow
                  label={t('onbType')}
                  // Пресет читается только ради НАЗВАНИЯ отрасли: его в строке организации
                  // нет. Правила при этом берутся из самой строки (AGENTS.md, правило 5).
                  value={industryName(presetFor(business.type), lang)}
                />
              </Card>
              <Hint>{t('setTypeNote')}</Hint>
            </div>

            <div className="px-4 pt-6">
              <SectionTitle>{t('setTitle')}</SectionTitle>
              <Card>
                {/* У частного мастера «Специалисты» прячутся: он там один, заведён при
                    регистрации, и список из самого себя выглядит недоделкой. Маршрут и экран
                    остаются — понадобятся в день, когда он наймёт помощника, и тогда флаг
                    снимается в настройках, а не переписывается кабинет. */}
                {TARGETS.filter(
                  (item) => !(business.isSolo && item.target === 'bizStaff'),
                ).map((item) => (
                  <NavRow
                    key={item.target}
                    label={t(item.labelKey)}
                    onClick={() => props.onOpen(item.target)}
                  />
                ))}
                <NavRow label={t('fbOpen')} onClick={props.onFeedback} />
              </Card>
            </div>

            {/* Архив — отдельным блоком внизу и спокойной кнопкой: рядом с «Опубликовать»
                он читался бы как ещё один переключатель видимости, а это уход с главного
                экрана. Не красная: действие обратимо. */}
            <div className="px-4 pb-4 pt-8">
              <CtaButton variant="ghost" onClick={() => setConfirmArchive(true)}>
                {t('bizArchive')}
              </CtaButton>
              <Hint>{t('bizArchiveHint')}</Hint>
            </div>
          </>
        ) : null}
      </ScrollArea>

      {business ? (
        <CtaBar withTabs>
          <CtaButton
            variant={business.isPublished ? 'ghost' : 'primary'}
            disabled={publishing}
            onClick={() => void togglePublish()}
          >
            {business.isPublished ? t('bizUnpublish') : t('bizPublish')}
          </CtaButton>
        </CtaBar>
      ) : null}

      <BottomSheet
        open={confirmArchive}
        title={t('bizArchiveT')}
        onClose={() => setConfirmArchive(false)}
      >
        <div className="mb-4 text-[13px] leading-[1.5] text-ink-2">{t('bizArchiveB')}</div>
        <div className="flex gap-2">
          <CtaButton variant="ghost" onClick={() => setConfirmArchive(false)}>
            {t('confirmNo')}
          </CtaButton>
          <CtaButton disabled={archiving} onClick={() => void archive()}>
            {t('bizArchive')}
          </CtaButton>
        </div>
      </BottomSheet>

      {/* Отказ публикации или архивации — в шторке, а не строкой в прокрутке: владелец нажал кнопку внизу
          и на текст, уехавший вверх экрана, не посмотрит (ТЗ §14.3, bottom sheet). */}
      <BottomSheet
        open={actionError !== null}
        title={t('errTitle')}
        onClose={() => setActionError(null)}
      >
        <ErrorState error={actionError} withTitle={false} />
        <CtaButton variant="ghost" onClick={() => setActionError(null)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>

      {current ? (
        <CityPickerSheet
          open={geoOpen}
          value={{ city: current.city ?? null, district: current.district ?? null }}
          clearLabel={t('geoCityNone')}
          onClose={() => setGeoOpen(false)}
          onApply={(next) => {
            edit({ city: next.city, district: next.district });
            setGeoOpen(false);
          }}
        />
      ) : null}
    </Screen>
  );
}

/**
 * Специализация частного мастера («Репетитор по литературе»): отдельное поле, чтобы
 * предмет не приходилось вписывать в имя. Хранится в `staff.title` его единственного
 * специалиста — список специалистов у соло скрыт, и править её больше негде.
 */
function SoloTitleField(props: { businessId: string }): JSX.Element | null {
  const { t } = useI18n();
  const api = useAppApi();
  const { state, reload } = useAsync(
    useCallback(() => api.cabinet.listStaff(props.businessId), [api, props.businessId]),
    [props.businessId],
  );
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const me = state.status === 'ready' ? state.data[0] : undefined;
  if (!me) return state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} /> : null;

  const value = draft ?? me.title ?? '';
  const changed = value.trim() !== (me.title ?? '');

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.cabinet.updateStaff(props.businessId, me.id, me.name, value.trim() || null);
      setDraft(null);
      reload();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Field label={t('stfTitle2')} value={value} placeholder={t('titlePh')} onChange={setDraft} />
      {changed ? (
        <div className="-mt-1 mb-3 flex">
          <SmallButton variant="accent" disabled={busy} onClick={() => void save()}>
            {t('setSave')}
          </SmallButton>
        </div>
      ) : null}
      {error ? <ErrorState error={error} /> : null}
    </>
  );
}
